import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getArchivedDocumentContent } from "../api/archive";
import { ApiError, type ApiBlobResponse } from "../api/client";
import { getInvoicePreview, issueInvoice } from "../api/invoices";
import { getMe, updateCompanyProfile } from "../api/me";
import { currentLocale, t } from "../lib/i18n";
import type {
  CompanyProfile,
  InvoiceDocumentKind,
  InvoicePreviewResponse,
  IssuedDocument,
  IssueResponse,
  MeResponse,
} from "../types";

const EMPTY_PROFILE: CompanyProfile = {
  company_name: null,
  company_postal_code: null,
  company_address: null,
  company_tel: null,
  company_registration_no: null,
};

/** 保存済み注文の税率内訳を確認し、帳票を発行して同じアーカイブへ保存する画面。 */
export function Invoices() {
  const params = useParams<{ documentId: string }>();
  const documentId = Number(params.documentId);
  const validDocumentId = Number.isSafeInteger(documentId) && documentId > 0;
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<CompanyProfile>(EMPTY_PROFILE);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [downloadId, setDownloadId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const previewQuery = useQuery({
    queryKey: ["invoice-preview", documentId],
    queryFn: () => getInvoicePreview(documentId),
    enabled: validDocumentId,
    retry: false,
  });
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe, retry: false });

  useEffect(() => {
    if (!meQuery.data || profileLoaded) return;
    setProfile(meQuery.data.company_profile);
    setProfileLoaded(true);
  }, [meQuery.data, profileLoaded]);

  const profileMutation = useMutation({
    mutationFn: updateCompanyProfile,
    onSuccess: async (saved) => {
      queryClient.setQueryData<MeResponse>(["me"], (current) =>
        current ? { ...current, company_profile: saved } : current,
      );
      setProfile(saved);
      setSuccessMessage(t("発行者情報を保存しました"));
      setActionError(null);
      await previewQuery.refetch();
    },
    onError: (error) => setActionError(error),
  });

  const issueMutation = useMutation<
    IssueResponse,
    Error,
    { kind: InvoiceDocumentKind; reissue: boolean }
  >({
    mutationFn: ({ kind, reissue }) => issueInvoice(documentId, kind, reissue),
    onSuccess: (response) => {
      queryClient.setQueryData<InvoicePreviewResponse>(
        ["invoice-preview", documentId],
        (current) => {
          if (!current) return current;
          const issued = current.issued.filter(
            (document) => document.kind !== response.document.kind,
          );
          issued.push(response.document);
          return {
            ...current,
            breakdown: response.breakdown,
            issued,
            usage: response.usage,
          };
        },
      );
      queryClient.setQueryData<MeResponse>(["me"], (current) =>
        current ? { ...current, invoice_usage: response.usage } : current,
      );
      setSuccessMessage(issueOutcomeMessage(response));
      setActionError(null);
    },
    onError: (error) => {
      setSuccessMessage(null);
      setActionError(error);
    },
  });

  function updateProfile(key: keyof CompanyProfile, value: string) {
    setProfile((current) => ({ ...current, [key]: value || null }));
  }

  async function download(document: IssuedDocument) {
    setDownloadId(document.document_id);
    setActionError(null);
    try {
      const file = await getArchivedDocumentContent(document.document_id, true);
      saveBlob(file, `denchobox-${document.kind}-${document.document_id}.pdf`);
    } catch (error) {
      setActionError(error);
    } finally {
      setDownloadId(null);
    }
  }

  const archiveLocation = { pathname: "/archive", search: location.search };
  if (!validDocumentId) {
    return (
      <Page title={t("請求書・領収書を作成")} backAction={{ content: t("アーカイブ検索"), onAction: () => navigate(archiveLocation) }}>
        <Banner tone="critical" title={t("注文を特定できませんでした")} />
      </Page>
    );
  }

  if (previewQuery.isPending || meQuery.isPending) {
    return (
      <Page title={t("請求書・領収書を作成")} backAction={{ content: t("アーカイブ検索"), onAction: () => navigate(archiveLocation) }}>
        <Card><SkeletonBodyText lines={10} /></Card>
      </Page>
    );
  }

  if (previewQuery.isError || meQuery.isError) {
    const error = previewQuery.error ?? meQuery.error;
    return (
      <Page title={t("請求書・領収書を作成")} backAction={{ content: t("アーカイブ検索"), onAction: () => navigate(archiveLocation) }}>
        <Banner
          tone="critical"
          title={t("発行前の内容を読み込めませんでした")}
          action={{
            content: t("再読み込み"),
            onAction: () => {
              void previewQuery.refetch();
              void meQuery.refetch();
            },
          }}
        >
          <Text as="p">{errorMessage(error)}</Text>
        </Banner>
      </Page>
    );
  }

  const preview = previewQuery.data;
  const invoice = preview.issued.find((item) => item.kind === "invoice");
  const receipt = preview.issued.find((item) => item.kind === "receipt");
  const quotaPlan = requiredPlanFromError(actionError) ?? preview.usage.required_plan_code;

  return (
    <Page
      title={t("請求書・領収書を作成")}
      subtitle={preview.order_name ?? t("保存済み注文から作成します")}
      backAction={{ content: t("アーカイブ検索"), onAction: () => navigate(archiveLocation) }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {successMessage ? (
              <Banner tone="success" title={successMessage} onDismiss={() => setSuccessMessage(null)} />
            ) : null}
            {actionError ? (
              <Banner
                tone="critical"
                title={t("処理に失敗しました")}
                onDismiss={() => setActionError(null)}
                action={isQuotaError(actionError) ? {
                  content: t("料金プランを見る"),
                  onAction: () => navigate({ pathname: "/plans", search: withRequiredPlan(location.search, quotaPlan) }),
                } : undefined}
              >
                <Text as="p">{errorMessage(actionError)}</Text>
              </Banner>
            ) : null}

            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" gap="300">
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">{t("税率別の発行内容")}</Text>
                    <Text as="p" tone="subdued">{t("発行前の確認です。この表示だけでは枚数を消費しません。")}</Text>
                  </BlockStack>
                  <Badge tone={preview.usage.over_limit ? "critical" : "info"}>
                    {usageLabel(preview.usage.count, preview.usage.limit)}
                  </Badge>
                </InlineStack>
                <DataTable
                  columnContentTypes={["text", "numeric", "numeric", "numeric"]}
                  headings={[t("税率"), t("税抜"), t("消費税"), t("税込")]}
                  rows={preview.breakdown.groups.map((group) => [
                    group.rate_label,
                    money(group.net_amount, preview.breakdown.currency),
                    money(group.tax_amount, preview.breakdown.currency),
                    money(group.gross_amount, preview.breakdown.currency),
                  ])}
                  showTotalsInFooter
                  totals={[t("合計"), money(preview.breakdown.net_total, preview.breakdown.currency), money(preview.breakdown.tax_total, preview.breakdown.currency), money(preview.breakdown.gross_total, preview.breakdown.currency)]}
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">{t("発行者情報")}</Text>
                  <Text as="p" tone="subdued">{t("PDFへ印字する情報です。請求書には名称と登録番号が必要です。")}</Text>
                </BlockStack>
                {preview.issuer_error ? <Banner tone="warning" title={t("請求書を発行する前に設定が必要です")}><Text as="p">{preview.issuer_error}</Text></Banner> : null}
                <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                  <TextField label={t("発行者名")} autoComplete="organization" value={profile.company_name ?? ""} onChange={(value) => updateProfile("company_name", value)} />
                  <TextField label={t("登録番号（T+13桁）")} autoComplete="off" value={profile.company_registration_no ?? ""} onChange={(value) => updateProfile("company_registration_no", value)} />
                  <TextField label={t("郵便番号")} autoComplete="postal-code" value={profile.company_postal_code ?? ""} onChange={(value) => updateProfile("company_postal_code", value)} />
                  <TextField label={t("電話番号")} type="tel" autoComplete="tel" value={profile.company_tel ?? ""} onChange={(value) => updateProfile("company_tel", value)} />
                </InlineGrid>
                <TextField label={t("住所")} autoComplete="street-address" value={profile.company_address ?? ""} onChange={(value) => updateProfile("company_address", value)} />
                <InlineStack align="end">
                  <Button variant="primary" loading={profileMutation.isPending} disabled={issueMutation.isPending} onClick={() => profileMutation.mutate(profile)}>
                    {t("発行者情報を保存")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>

            {preview.usage.over_limit && (!invoice || !receipt) ? (
              <Banner
                tone="warning"
                title={t("今月の発行上限に達しています")}
                action={{ content: t("料金プランを見る"), onAction: () => navigate({ pathname: "/plans", search: withRequiredPlan(location.search, quotaPlan) }) }}
              >
                <Text as="p">{t("新しい発行は停止しますが、発行済み書類の取得と訂正版の再発行は続けられます。")}</Text>
              </Banner>
            ) : null}

            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
              <IssueCard
                kind="invoice"
                document={invoice}
                canCreate={preview.can_issue_invoice && !preview.usage.over_limit}
                busy={issueMutation.isPending || profileMutation.isPending}
                downloading={downloadId === invoice?.document_id}
                onIssue={(reissue) => issueMutation.mutate({ kind: "invoice", reissue })}
                onDownload={() => invoice && void download(invoice)}
              />
              <IssueCard
                kind="receipt"
                document={receipt}
                canCreate={Boolean(meQuery.data.company_profile.company_name) && !preview.usage.over_limit}
                busy={issueMutation.isPending || profileMutation.isPending}
                downloading={downloadId === receipt?.document_id}
                onIssue={(reissue) => issueMutation.mutate({ kind: "receipt", reissue })}
                onDownload={() => receipt && void download(receipt)}
              />
            </InlineGrid>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function IssueCard({ kind, document, canCreate, busy, downloading, onIssue, onDownload }: {
  kind: InvoiceDocumentKind;
  document: IssuedDocument | undefined;
  canCreate: boolean;
  busy: boolean;
  downloading: boolean;
  onIssue: (reissue: boolean) => void;
  onDownload: () => void;
}) {
  const label = kind === "invoice" ? t("適格請求書") : t("領収書");
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="200">
          <Text as="h2" variant="headingMd">{label}</Text>
          {document ? <Badge tone={document.is_stale ? "warning" : "success"}>{document.is_stale ? t("注文の訂正あり") : t("発行済み")}</Badge> : <Badge>{t("未発行")}</Badge>}
        </InlineStack>
        <Text as="p" tone="subdued">
          {kind === "invoice" ? t("税率ごとの対価と消費税額、登録番号を記載します。") : t("入金の受領を示すPDFを作成します。登録番号がある場合は併記します。")}
        </Text>
        {document ? (
          <BlockStack gap="200">
            <Text as="p" tone="subdued">{t("発行日時: {date}", { date: formatDateTime(document.issued_at) })}</Text>
            {document.is_stale ? <Text as="p" tone="caution">{t("発行後に元の注文が訂正されています。内容を確認してから修正版を再発行してください。")}</Text> : null}
            <InlineStack gap="200">
              <Button variant="primary" loading={downloading} disabled={busy} onClick={onDownload}>{t("PDFを取得")}</Button>
              {document.is_stale ? <Button loading={busy} disabled={downloading} onClick={() => onIssue(true)}>{t("修正版を再発行")}</Button> : null}
            </InlineStack>
          </BlockStack>
        ) : (
          <Button variant="primary" disabled={!canCreate || busy} loading={busy} onClick={() => onIssue(false)}>{t("{kind}を発行", { kind: label })}</Button>
        )}
      </BlockStack>
    </Card>
  );
}

function issueOutcomeMessage(response: IssueResponse): string {
  if (response.outcome === "reissued") return t("修正版を発行し、アーカイブへ保存しました");
  if (response.outcome === "existing") return t("発行済みの書類を表示しました");
  return t("書類を発行し、アーカイブへ保存しました");
}

function usageLabel(count: number, limit: number | null): string {
  return limit === null ? t("今月 {count}枚 / 無制限", { count }) : t("今月 {count} / {limit}枚", { count, limit });
}

function money(value: string, currency: string): string {
  const [integer, decimal] = value.split(".", 2);
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${currency} ${decimal ? `${grouped}.${decimal}` : grouped}`;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat(currentLocale() === "en" ? "en-US" : "ja-JP", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  return error instanceof Error ? error.message : String(error);
}

function isQuotaError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 402;
}

function requiredPlanFromError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const detail = (error.body as { detail?: { required_plan_code?: unknown } } | undefined)?.detail;
  return typeof detail?.required_plan_code === "string" ? detail.required_plan_code : null;
}

function withRequiredPlan(search: string, plan: string): string {
  const query = new URLSearchParams(search);
  query.set("required", plan);
  return `?${query.toString()}`;
}

function saveBlob(file: ApiBlobResponse, fallbackName: string): void {
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = file.filename || fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
